import React, { useEffect } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@love-irish/ui-components/dialog"
import Button from "@love-irish/core-button"
import { IconSparkles } from "@tabler/icons-react"
import analytics from "@love-irish/analytics"
import { ANALYTICS_EVENT_NAMES } from "@love-irish/analytics/events"

export interface UpsellFeature {
  icon: React.ComponentType<{ size?: number; className?: string }>
  title: string
  description: string
}

export interface UpsellCta {
  label: string
  href?: string
  onClick?: () => void
  icon?: React.ComponentType<{ size?: number; className?: string }>
}

export interface UpsellInfoBanner {
  icon?: React.ComponentType<{ size?: number; className?: string }>
  text: React.ReactNode
}

export interface UpsellModalProps {
  // Control
  isOpen: boolean
  onClose: () => void

  // Analytics
  featureName: string
  analyticsProperties?: Record<string, unknown>

  // Content
  icon?: React.ReactNode
  headerGradient?: string
  title: string
  subtitle?: string
  description: string

  // Features list (optional)
  features?: UpsellFeature[]

  // Info banner (optional)
  infoBanner?: UpsellInfoBanner

  // CTAs
  primaryCta: UpsellCta
  secondaryCta?: UpsellCta

  // Footer
  footerNote?: string
}

const DEFAULT_GRADIENT = "bg-linear-to-r from-success-500 to-teal-600"

export const UpsellModal: React.FC<UpsellModalProps> = ({
  isOpen,
  onClose,
  featureName,
  analyticsProperties = {},
  icon,
  headerGradient = DEFAULT_GRADIENT,
  title,
  subtitle,
  description,
  features,
  infoBanner,
  primaryCta,
  secondaryCta,
  footerNote,
}) => {
  // Track when modal is opened
  useEffect(() => {
    if (isOpen) {
      analytics.track(ANALYTICS_EVENT_NAMES.FEATURE_UPSELL_VIEWED, {
        feature: featureName,
        path: window.location.pathname,
        ...analyticsProperties,
      })
    }
  }, [isOpen, featureName, analyticsProperties])

  const headerIcon = icon ?? <IconSparkles size={32} />

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="p-0 overflow-hidden max-w-md border-0"
        closeButtonClass="text-white"
      >
        {/* Header */}
        <DialogHeader>
          <div className={`${headerGradient} px-6 py-8 text-center text-white`}>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white/20">
              {headerIcon}
            </div>
            <DialogHeader className="text-center">
              <DialogTitle className="text-2xl font-bold text-white text-center">
                {title}
              </DialogTitle>
              <DialogDescription
                className={
                  subtitle ? "mt-2 text-white/80 text-center" : "sr-only"
                }
              >
                {subtitle || description}
              </DialogDescription>
            </DialogHeader>
          </div>
        </DialogHeader>

        {/* Content */}
        <div className="px-6 py-6">
          <p className="text-center text-gray-600 dark:text-gray-300 mb-8">
            {description}
          </p>

          {/* Info Banner */}
          {infoBanner && (
            <div className="flex items-center justify-center gap-2 text-sm text-warning-800 dark:text-warning-400 bg-warning-100 dark:bg-warning-900/30 py-2 px-3 rounded-md">
              {infoBanner.icon && (
                <infoBanner.icon size={14} className="shrink-0" />
              )}
              <span>{infoBanner.text}</span>
            </div>
          )}

          {/* Features list */}
          {features && features.length > 0 && (
            <ul className="space-y-4 mb-6 mt-6">
              {features.map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="shrink-0 mt-0.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30">
                      <feature.icon
                        size={16}
                        className="text-emerald-600 dark:text-emerald-400"
                      />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {feature.description}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* CTA Buttons */}
          <DialogFooter className="mt-6">
            {primaryCta.href ? (
              <Button as="a" href={primaryCta.href} className="w-full">
                {primaryCta.icon && <primaryCta.icon size={14} />}
                {primaryCta.label}
              </Button>
            ) : (
              <Button onClick={primaryCta.onClick} className="w-full">
                {primaryCta.icon && <primaryCta.icon size={14} />}
                {primaryCta.label}
              </Button>
            )}
            {secondaryCta &&
              (secondaryCta.href ? (
                <Button
                  as="a"
                  href={secondaryCta.href}
                  variant="outline"
                  className="w-full"
                >
                  {secondaryCta.icon && <secondaryCta.icon size={14} />}
                  {secondaryCta.label}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={secondaryCta.onClick}
                  className="w-full"
                >
                  {secondaryCta.icon && <secondaryCta.icon size={14} />}
                  {secondaryCta.label}
                </Button>
              ))}
          </DialogFooter>
        </div>

        {/* Footer note */}
        {footerNote && (
          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-gray-50 dark:bg-gray-800/50">
            <p className="text-center text-xs text-gray-500 dark:text-gray-400">
              {footerNote}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default UpsellModal
